import { Avatar, Badge, Box, Button, IconButton, Menu, MenuItem, Typography } from "@mui/material"
import { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { getConfiguration } from "../../clients/configuration"
import { GenericSpinner } from "../../components/GenericSpinner/GenericSpinner"
import { Account, AccountsApi, AccountStatus } from "../../clients"
import {
    MoreHorizOutlined as MoreHorizOutlinedIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    CancelOutlined as CancelOutlinedIcon,
    AddModeratorOutlined as AddModeratorOutlinedIcon,
    RemoveModeratorOutlined as RemoveModeratorOutlinedIcon,
} from "@mui/icons-material"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { addAlertWithTimeout } from "../../redux/features/alerts/alertsHelpers"
import { AppAlert } from "../../components/AppAlerts/AppAlerts"

export const AccountDetail = () => {

    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const currentUser = useAppSelector(state => state.currentUser)

    const { accountID } = useParams<{ accountID: string }>()
    const [account, setAccount] = useState<Account | null>(null)
    const [loading, setLoading] = useState(true)
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)

    const contactInfoString = account ? [account.email, account.phoneNumber].filter(x => !!x).join(' | ') : ''
    const currentUserIsAdmin = currentUser.account && (currentUser.account.role === 'admin' || currentUser.account.role === 'superadmin')
    const userIsSuperAdmin = currentUser.account && currentUser.account.role === 'superadmin'

    const showBadge = () => {
        if (currentUserIsAdmin && account.status === AccountStatus.AwaitingApproval) {
            return '!'
        }
        return null
    }

    const getAccountsApi = async () => {
        const config = await getConfiguration()
        return new AccountsApi(config)
    }

    const loadAccount = useCallback(async () => {
        const accountsApi = await getAccountsApi()
        setLoading(true)
        try {
            const account = await accountsApi.getAccount({ accountID })
            setAccount(account.data)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }, [accountID])

    const handleCloseMoreMenu = () => {
        setMenuAnchorEl(null)
    }

    const handleEnableAccount = async () => {
        const accountsApi = await getAccountsApi()
        try {
            const account = await accountsApi.enableAccount({ accountID })
            setAccount(account.data)
            addAlertWithTimeout(dispatch, new AppAlert('success', 'Account enabled'))
            handleCloseMoreMenu()
        } catch {
            addAlertWithTimeout(dispatch, new AppAlert('error', 'Failed to enable account'))
        }
    }

    const handleDisableAccount = async () => {
        const accountsApi = await getAccountsApi()
        try {
            const account = await accountsApi.disableAccount({ accountID })
            setAccount(account.data)
            addAlertWithTimeout(dispatch, new AppAlert('success', 'Account disabled'))
            handleCloseMoreMenu()
        } catch {
            addAlertWithTimeout(dispatch, new AppAlert('error', 'Failed to disable account'))
        }
    }

    const handleAdminAccount = async () => {
        const accountsApi = await getAccountsApi()
        try {
            const account = await accountsApi.grantAdminToAccount({ accountID })
            setAccount(account.data)
            addAlertWithTimeout(dispatch, new AppAlert('success', 'Granted admin to account'))
            handleCloseMoreMenu()
        } catch {
            addAlertWithTimeout(dispatch, new AppAlert('error', 'Failed to grant admin to account'))
        }
    }

    const handleRevokeAdmin = async () => {
        const accountsApi = await getAccountsApi()
        try {
            const account = await accountsApi.revokeAdminFromAccount({ accountID })
            setAccount(account.data)
            addAlertWithTimeout(dispatch, new AppAlert('success', 'Revoked admin from account'))
            handleCloseMoreMenu()
        } catch {
            addAlertWithTimeout(dispatch, new AppAlert('error', 'Failed to revoke admin from account'))
        }
    }

    useEffect(() => {
        loadAccount()
    }, [loadAccount])

    if (loading) {
        return <GenericSpinner />
    }

    if (!account) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', mt: 3 }}>
                <Typography variant='h3' sx={{ fontSize: '1.5rem', fontWeight: 400, }}>
                    Whoops!
                </Typography>
                <Typography variant='body1' sx={{ fontSize: '1rem', fontWeight: 200, mt: 3 }}>
                    We couldn't find that account
                </Typography>
                <Button
                    variant='contained'
                    sx={{ mt: 3 }}
                    onClick={() => navigate('/')}
                >
                    Return Home
                </Button>
            </Box>
        )
    }

    return (
        <Box className='account-detail__container' sx={{ display: 'flex', p: 3 }}>
            <Box className='account-detail__sidebar' sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar className='account-detail__avatar'
                        sx={{
                            bgcolor: 'primary.main',
                            mx: 2,
                            height: '60px',
                            width: '60px',
                            fontSize: '32px'
                        }}>{account.username.charAt(0).toUpperCase()}</Avatar>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant='h3' sx={{ fontSize: '1.5rem', fontWeight: 200, ml: 3 }}>{account.givenName} {account.familyName}</Typography>
                        <Typography variant='caption' sx={{ fontSize: '1rem', fontWeight: 200, ml: 3 }}>{account.username}</Typography>
                        <Typography variant='caption' sx={{ fontSize: '1rem', fontWeight: 200, ml: 3 }}>{contactInfoString}</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
                    <Box>
                        {
                            currentUserIsAdmin &&
                            <Badge color='error' badgeContent={showBadge()}>
                                <IconButton onClick={e => setMenuAnchorEl(e.currentTarget)}>
                                    <MoreHorizOutlinedIcon />
                                </IconButton>
                            </Badge>
                        }
                        <Menu
                            id="menu-account-options"
                            anchorEl={menuAnchorEl}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            open={Boolean(menuAnchorEl)}
                            onClose={handleCloseMoreMenu}
                            keepMounted
                        >
                            {
                                currentUserIsAdmin && account.status !== AccountStatus.Enabled &&
                                <MenuItem onClick={handleEnableAccount}>
                                    <CheckCircleOutlineIcon sx={{ mr: 1 }} /> Enable Account
                                </MenuItem>
                            }
                            {
                                currentUserIsAdmin && account.status !== AccountStatus.Disabled &&
                                <MenuItem onClick={handleDisableAccount}>
                                    <CancelOutlinedIcon sx={{ mr: 1 }} /> Disable Account
                                </MenuItem>
                            }
                            {
                                userIsSuperAdmin && account.role !== 'superadmin' && account.status === AccountStatus.Enabled &&
                                account.role !== 'admin' &&
                                <MenuItem onClick={handleAdminAccount}>
                                    <AddModeratorOutlinedIcon sx={{ mr: 1 }} /> Grant Admin
                                </MenuItem>
                            }
                            {
                                userIsSuperAdmin && account.role !== 'superadmin' && account.status === AccountStatus.Enabled &&
                                account.role === 'admin' &&
                                <MenuItem onClick={handleRevokeAdmin}>
                                    <RemoveModeratorOutlinedIcon sx={{ mr: 1 }} /> Revoke Admin
                                </MenuItem>
                            }
                        </Menu>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}